import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MobileNav from "../components/MobileNav";
import Footer1 from "../components/Footer1";
import "./PrintDetails.css";

// ✅ Import local JSON data
import paintingsData from "../data/paintings.json"; 

const getDynamicSizes = (sizeStr) => {
    // Fallback default sizes if no size string is provided
    const defaultSizes = ["Standard (24x36\")", "Medium (16x24\")", "Small (12x18\")"];
    if (!sizeStr) return defaultSizes;
    
    // Extract numbers from strings like "36'' X 48''" or "48'' X 72''"
    const matches = sizeStr.match(/(\d+)/g);
    if (!matches || matches.length < 2) return defaultSizes;
    
    const w = parseInt(matches[0]);
    const h = parseInt(matches[1]);
    
    // Helper to format numbers cleanly (removes decimals if it's a whole number)
    const formatNum = (num) => Number.isInteger(num) ? num : num.toFixed(1).replace(/\.0$/, '');

    return [
        `Original Size (${w}x${h}")`,               // Exact live size
        `Medium (${formatNum(w * 0.66)}x${formatNum(h * 0.66)}")`, // Roughly 2/3 size
        `Small (${formatNum(w * 0.5)}x${formatNum(h * 0.5)}")`     // Exactly 1/2 size
    ];
};

const PrintDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [printData, setPrintData] = useState(null);
    const [relatedPrints, setRelatedPrints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addedToCart, setAddedToCart] = useState(false);
    
    // Interactive selections
    const [dynamicSizes, setDynamicSizes] = useState([]);
    const [selectedSize, setSelectedSize] = useState("");
    const [selectedFrame, setSelectedFrame] = useState("Unframed (Rolled Archival Canvas)");
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    
    // Story Expansion State
    const [isStoryExpanded, setIsStoryExpanded] = useState(false);

    // Fetch the specific painting and related items
    useEffect(() => {
        window.scrollTo(0, 0);
        
        // 1. Lock screen scroll & set loading to true
        setLoading(true);
        document.body.style.overflow = "hidden";
        
        // Reset story expansion when a new print is loaded
        setIsStoryExpanded(false);
        
        // 2. Fetch the data immediately so the DOM can build in the background
        const foundPrint = paintingsData.find((p) => p.id === parseInt(id));
        setPrintData(foundPrint || null);

        if (foundPrint) {
            // Generate dynamic sizes based on the original painting's proportions
            const generatedSizes = getDynamicSizes(foundPrint.size);
            setDynamicSizes(generatedSizes);
            setSelectedSize(generatedSizes[0]); // Default to Standard Size

            // Find related prints in the same category
            const related = paintingsData
                .filter(p => p.category === foundPrint.category && p.id !== foundPrint.id)
                .slice(0, 3);
            setRelatedPrints(related);
        }

        setAddedToCart(false);

        // 3. Keep the aesthetic loader on screen for 1.8 seconds while images fetch in background
        const timer = setTimeout(() => {
            setLoading(false);
            document.body.style.overflow = "auto"; // Unlock scroll
        }, 1800);

        return () => {
            clearTimeout(timer);
            document.body.style.overflow = "auto";
        };
    }, [id]);

    const fadeUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.3 } }
    };

    if (!printData && !loading) {
        return (
            <div className="elite-print-page">
                <MobileNav />
                <div className="elite-empty-state" style={{ marginTop: "20vh" }}>
                    <p>Masterwork not found in the archives.</p>
                    <button onClick={() => navigate("/allPrints")} className="btn-elite-secondary mt-4">Return to Archive</button>
                </div>
            </div>
        );
    }

    const getPrice = () => {
        // Safe check in case selectedSize hasn't populated yet
        if (!selectedSize) return 25000;

        let basePrice = 25000; // Default for Standard
        if (selectedSize.includes("Medium")) basePrice = 15000; 
        if (selectedSize.includes("Small")) basePrice = 9500;   
        
        let framePrice = selectedFrame.includes("Unframed") ? 0 : 8500;
        return basePrice + framePrice;
    };

    const handleAddToPortfolio = () => {
        const existingPortfolio = JSON.parse(localStorage.getItem('userPortfolio')) || [];
        
        const newItem = {
            ...printData,
            cartId: Date.now(), 
            selectedSize,
            selectedFrame,
            cartPrice: getPrice()
        };
        
        existingPortfolio.push(newItem);
        localStorage.setItem('userPortfolio', JSON.stringify(existingPortfolio));
        
        setAddedToCart(true);
        setTimeout(() => setAddedToCart(false), 3000); 
    };

    const images = printData?.images || [];
    const mainImageUrl = images[activeImageIndex]?.imageUrl || "https://via.placeholder.com/600x800?text=Masterpiece";
    const interiorImage = images.length >= 3 ? images[2].imageUrl : null;
    
    const whatsappMessage = `Dear Studio,\n\nI would like to acquire a Limited Edition Print of '${printData?.title}'.\n\nConfiguration:\n- Size: ${selectedSize}\n- Frame: ${selectedFrame}\n- Price: ₹${getPrice().toLocaleString("en-IN")}\n\nPlease share the payment and shipping details.`;

    return (
        <div className="elite-print-page">
            <AnimatePresence>
                {loading && (
                    <motion.div
                        key="elite-loader"
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                        style={{
                            position: "fixed",
                            inset: 0,
                            zIndex: 99999,
                            backgroundColor: "#faf9f6",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center"
                        }}
                    >
                        <motion.span
                            animate={{ opacity: [0.3, 1, 0.3] }}
                            transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                            style={{
                                fontFamily: "'Cinzel', serif",
                                fontSize: "1rem",
                                textTransform: "uppercase",
                                letterSpacing: "6px",
                                color: "#111111",
                                marginBottom: "20px"
                            }}
                        >
                            Curating Archival Print
                        </motion.span>
                        <div style={{ width: "80px", height: "1px", backgroundColor: "rgba(0,0,0,0.05)", overflow: "hidden", position: "relative" }}>
                            <motion.div
                                initial={{ x: "-100%" }}
                                animate={{ x: "100%" }}
                                transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    backgroundColor: "#88b019",
                                    position: "absolute"
                                }}
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <MobileNav />

            {printData && (
                <>
                    {interiorImage ? (
                        <section className="print-interior-hero">
                            <motion.div 
                                className="interior-hero-bg" 
                                style={{ backgroundImage: `url(${interiorImage})` }}
                                initial={{ scale: 1.05 }}
                                animate={{ scale: !loading ? 1 : 1.05 }}
                                transition={{ duration: 1.5, ease: "easeOut", delay: 0.3 }}
                            ></motion.div>
                            <div className="interior-hero-overlay"></div>
                            <motion.div className="interior-hero-content" initial="hidden" animate={!loading ? "visible" : "hidden"} variants={fadeUp}>
                                <span className="interior-eyebrow">Archival Preview</span>
                                <h2>{printData.title}</h2>
                            </motion.div>
                        </section>
                    ) : (
                        <div className="nav-spacer"></div>
                    )}

                    <div className="print-details-container">
                        {/* LEFT: Visual Presentation */}
                        <motion.div className="print-visuals" initial="hidden" animate={!loading ? "visible" : "hidden"} variants={fadeUp}>
                            <div className="print-main-image-wrapper">
                                <img 
                                    src={mainImageUrl} 
                                    alt={printData.title} 
                                    className="print-main-image"
                                />
                            </div>
                            
                            {images.length > 1 && (
                                <div className="print-thumbnails">
                                    {images.map((img, index) => (
                                      index < 2 &&
                                      <img 
                                            key={img.id || index}
                                            src={img.imageUrl} 
                                            alt={`Detail ${index + 1}`} 
                                            className={activeImageIndex === index ? "active-thumb" : ""}
                                            onClick={() => setActiveImageIndex(index)}
                                        />
                                    ))}
                                </div>
                            )}
                        </motion.div>

                        {/* RIGHT: Bespoke Configurator */}
                        <motion.div className="print-configurator" initial="hidden" animate={!loading ? "visible" : "hidden"} variants={fadeUp}>
                            <span className="curatorial-tag-gold">Limited Edition of 50</span>
                            <h1 className="print-title">{printData.title}</h1>
                            <p className="print-artist">Archival Reproduction by Anjaan Vivek</p>

                            <div className="print-description">
                                <p>Museum-grade giclée print on 310gsm Hahnemühle archival paper. Each edition is accompanied by a studio-stamped Certificate of Authenticity to ensure provenance and long-term investment value.</p>
                                
                                {printData.story && (
                                    <div style={{ position: "relative", marginTop: "20px" }}>
                                        <div 
                                            className="print-story-html"
                                            style={{
                                                maxHeight: isStoryExpanded ? "2500px" : "140px",
                                                overflow: "hidden",
                                                transition: "max-height 0.6s ease-in-out",
                                                position: "relative"
                                            }}
                                            dangerouslySetInnerHTML={{ __html: printData.story }} 
                                        />
                                        
                                        {!isStoryExpanded && (
                                            <div style={{
                                                position: "absolute",
                                                bottom: 0,
                                                left: 0,
                                                width: "100%",
                                                height: "60px",
                                                background: "linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))",
                                                pointerEvents: "none"
                                            }}></div>
                                        )}
                                        
                                        <button 
                                            onClick={() => setIsStoryExpanded(!isStoryExpanded)}
                                            style={{
                                                background: "none",
                                                border: "none",
                                                color: "#111", 
                                                cursor: "pointer",
                                                padding: "12px 0 0 0",
                                                fontFamily: "'Cinzel', serif",
                                                fontSize: "0.85rem",
                                                fontWeight: "bold",
                                                textTransform: "uppercase",
                                                letterSpacing: "1px",
                                                textDecoration: "underline",
                                                textUnderlineOffset: "4px"
                                            }}
                                        >
                                            {isStoryExpanded ? "Read Less" : "Read the Full Story"}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* CONFIG 1: SIZE */}
                            <div className="config-section" style={{ marginTop: "30px" }}>
                                <span className="config-label">1. Select Dimensions</span>
                                <div className="options-grid">
                                    {dynamicSizes.map(size => (
                                        <button 
                                            key={size}
                                            className={`config-btn ${selectedSize === size ? 'active' : ''}`}
                                            onClick={() => setSelectedSize(size)}
                                        >
                                            {size}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* CONFIG 2: FRAME */}
                            <div className="config-section">
                                <span className="config-label">2. Select Presentation</span>
                                <div className="options-grid">
                                    {["Unframed (Rolled Archival Canvas)", "Museum Black Ash Frame", "Natural Oak Frame"].map(frame => (
                                        <button 
                                            key={frame}
                                            className={`config-btn ${selectedFrame === frame ? 'active' : ''}`}
                                            onClick={() => setSelectedFrame(frame)}
                                        >
                                            {frame}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="print-acquisition-footer">
                                <div className="print-price">
                                    <span className="price-label">Acquisition Value</span>
                                    <span className="price-amount">₹ {getPrice().toLocaleString("en-IN")}</span>
                                </div>
                                
                                <div className="print-actions-wrapper">
                                    <button 
                                        className="btn-elite-outline print-cart-btn"
                                        onClick={handleAddToPortfolio}
                                    >
                                        {addedToCart ? "Added to Portfolio ✓" : "Add to Portfolio"}
                                    </button>
                                    
                                    <a 
                                        href={`https://wa.me/919300847841?text=${encodeURIComponent(whatsappMessage)}`} 
                                        target="_blank" 
                                        rel="noreferrer" 
                                        className="btn-elite-primary print-acquire-btn"
                                    >
                                        Secure via Concierge
                                    </a>
                                </div>
                            </div>

                            <div className="trust-indicators">
                                <div className="trust-item">
                                    <i className="fa-solid fa-certificate"></i>
                                    <span>Includes Certificate of Authenticity</span>
                                </div>
                                <div className="trust-item">
                                    <i className="fa-solid fa-box-archive"></i>
                                    <span>White-Glove Archival Shipping</span>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {relatedPrints.length > 0 && (
                        <div className="related-prints-section">
                            <motion.div className="related-header" initial="hidden" animate={!loading ? "visible" : "hidden"} variants={fadeUp}>
                                <span className="curatorial-tag-gold">Curated Recommendations</span>
                                <h2>More from {printData.category}</h2>
                            </motion.div>
                            
                            <motion.div className="related-prints-grid" initial="hidden" animate={!loading ? "visible" : "hidden"} variants={fadeUp}>
                                {relatedPrints.map(related => (
                                    <div 
                                        key={related.id} 
                                        className="related-card"
                                        onClick={() => navigate(`/printDetails/${related.id}`)}
                                    >
                                        <div className="related-img-wrapper">
                                            <img 
                                                src={related.images?.[0]?.imageUrl || "https://via.placeholder.com/600x800"} 
                                                alt={related.title} 
                                            />
                                            <div className="related-hover">
                                                <span>View Details</span>
                                            </div>
                                        </div>
                                        <h4>{related.title}</h4>
                                        <span>{related.category}</span>
                                    </div>
                                ))}
                            </motion.div>
                        </div>
                    )}
                </>
            )}

            <Footer1 />
        </div>
    );
};

export default PrintDetails;